import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiVerifyAccessCode } from '../config/api';
import { ROUTES } from '../constants/routes';
import {
  clearStoredAccessCode,
  getStoredAccessCode,
} from '../screens/AccessCodeScreen';
import { isPublicProductionHost } from '../utils/publicHost';

const SELF_SERVE_PUBLIC_PATHS = new Set([
  ROUTES.LOGIN,
  ROUTES.JOIN,
  ROUTES.ACCESS_CODE,
  ROUTES.DOCTOR_SIGNUP,
  ROUTES.DOCTOR_LOGIN,
]);

function isSelfServePublicPath(pathname) {
  return (
    SELF_SERVE_PUBLIC_PATHS.has(pathname) ||
    pathname === '/join' ||
    pathname.startsWith('/doctor/signup') ||
    pathname.startsWith('/doctor/login')
  );
}

function ProductionAccessGate({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      const code = getStoredAccessCode();
      if (!code) {
        if (!cancelled) setStatus('invalid');
        return;
      }

      try {
        await apiVerifyAccessCode();
        if (!cancelled) setStatus('valid');
      } catch {
        clearStoredAccessCode();
        if (!cancelled) setStatus('invalid');
      }
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (location.pathname === ROUTES.ACCESS_CODE) {
    if (status === 'valid') {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }
    return children;
  }

  if (status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-primary-light border-t-primary"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (status === 'invalid') {
    return <Navigate to={ROUTES.ACCESS_CODE} replace />;
  }

  return children;
}

function SelfServeAccessGate({ children }) {
  const location = useLocation();
  const jwt = typeof window !== 'undefined' ? localStorage.getItem('niva_jwt') : '';
  const [status, setStatus] = useState(() => {
    if (isSelfServePublicPath(location.pathname) || jwt) return 'valid';
    return 'checking';
  });

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (isSelfServePublicPath(location.pathname) || jwt) {
        if (!cancelled) setStatus('valid');
        return;
      }

      const code = getStoredAccessCode();
      if (!code) {
        if (!cancelled) setStatus('invalid');
        return;
      }

      try {
        await apiVerifyAccessCode();
        if (!cancelled) setStatus('valid');
      } catch {
        clearStoredAccessCode();
        if (!cancelled) setStatus('invalid');
      }
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [jwt, location.pathname]);

  if (isSelfServePublicPath(location.pathname)) return children;
  if (jwt) return children;

  if (status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  if (status === 'invalid') {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return children;
}

export default function AccessGate({ children }) {
  if (isPublicProductionHost()) {
    return <ProductionAccessGate>{children}</ProductionAccessGate>;
  }
  return <SelfServeAccessGate>{children}</SelfServeAccessGate>;
}
